// SharePopup.js (Step 2: Add Copy Logic with Tick Animation)
import React, { useEffect, useRef, useState } from 'react';
import { X, Copy, Check } from 'lucide-react';
import { FaWhatsapp, FaTelegramPlane } from 'react-icons/fa';
 
/**
 * SharePopup Component
 * Props:
 * - url: string - the full shareable URL
 * - onClose: function - called when popup is dismissed
 * - title: optional string - defaults to 'Share'
 * - pdfUrl: string - the URL of the PDF
 * - mode: string - the mode of the popup ('resource' or 'page')
 * - resourceType: string - the type of the resource
 * - resourceTitle: string - the title of the resource
 */
export default function SharePopup({ url, onClose, title = 'Share', mode = 'page', resourceType, resourceTitle }) {
  const popupRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  // Close popup on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Handle copy click
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2s
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  // Compose WhatsApp message for resource
  let whatsappMessage = url;
  if (mode === 'resource' && resourceType && resourceTitle) {
    whatsappMessage = `I enjoyed this ${resourceType}: ${resourceTitle} at ${url}. If you want to explore other resources checkout https://padhojalna.in/resources.`;
  }

  // Resource card share popup: two rows, title, link+copy, then WhatsApp/Telegram
  if (mode === 'resource' && !isMobile) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <div
          ref={popupRef}
          className="bg-white rounded-xl shadow-xl p-4 max-w-xs w-full px-4 relative animate-fadeIn flex flex-col items-center"
        >
          <button
            onClick={onClose}
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            aria-label="Close share popup"
          >
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold text-gray-800 mb-3 text-center">Share</h2>
          <div className="flex items-center w-full bg-gray-100 rounded px-2 py-1 mb-3">
            <input
              type="text"
              readOnly
              value={url}
              className="text-sm text-gray-700 bg-gray-100 w-full mr-2 outline-none cursor-default"
              aria-label="Share URL"
            />
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(url);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                } catch (err) {
                  console.error('Failed to copy URL:', err);
                }
              }}
              className="text-gray-600 hover:text-gray-800 focus:outline-none"
              aria-label="Copy link"
            >
              {copied ? <Check className="w-6 h-6 text-green-600" /> : <Copy className="w-6 h-6" />}
            </button>
          </div>
          <div className="flex justify-center items-center gap-6 mt-1 mb-2">
            <a
              href={`https://api.whatsapp.com/send?text=${encodeURIComponent(whatsappMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 hover:bg-green-200 transition"
              aria-label="Share on WhatsApp"
            >
              <FaWhatsapp className="w-8 h-8 text-green-600" />
            </a>
            <a
              href={`https://t.me/share/url?url=${encodeURIComponent(url)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 hover:bg-blue-200 transition"
              aria-label="Share on Telegram"
            >
              <FaTelegramPlane className="w-8 h-8 text-blue-600" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Mobile: compact version for both resource and page share popups
  if (isMobile) {
    // Use a different title for page vs resource
    const popupTitle = mode === 'page' ? 'Share this page' : 'Share';
    return (
      <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
        <div
          ref={popupRef}
          className="bg-white rounded-xl shadow-xl p-4 max-w-xs w-full px-4 relative animate-fadeIn flex flex-col items-center"
        >
          <button
            onClick={onClose}
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            aria-label="Close share popup"
          >
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold text-gray-800 mb-3 text-center">{popupTitle}</h2>
          <div className="flex items-center w-full bg-gray-100 rounded px-2 py-1 mb-3">
            <input
              type="text"
              readOnly
              value={url}
              className="text-sm text-gray-700 bg-gray-100 w-full mr-2 outline-none cursor-default"
              aria-label="Share URL"
            />
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(url);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                } catch (err) {
                  console.error('Failed to copy URL:', err);
                }
              }}
              className="text-gray-600 hover:text-gray-800 focus:outline-none"
              aria-label="Copy link"
            >
              {copied ? <Check className="w-6 h-6 text-green-600" /> : <Copy className="w-6 h-6" />}
            </button>
          </div>
          <div className="flex justify-center items-center gap-6 mt-1 mb-2">
            <a
              href={`https://api.whatsapp.com/send?text=${encodeURIComponent(whatsappMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 hover:bg-green-200 transition"
              aria-label="Share on WhatsApp"
            >
              <FaWhatsapp className="w-8 h-8 text-green-600" />
            </a>
            <a
              href={`https://t.me/share/url?url=${encodeURIComponent(url)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 hover:bg-blue-200 transition"
              aria-label="Share on Telegram"
            >
              <FaTelegramPlane className="w-8 h-8 text-blue-600" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div
        ref={popupRef}
        className="bg-white rounded-xl shadow-xl p-4 max-w-sm w-full mx-auto relative animate-fadeIn"
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
          aria-label="Close share popup"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold text-gray-800 mb-4 text-center">{title}</h2>

        {/* Share Options */}
        <div className="space-y-3">
          <div className="flex items-center justify-between bg-gray-100 rounded px-3 py-2">
            <input
              type="text"
              readOnly
              value={url}
              className="text-sm text-gray-700 bg-gray-100 w-full mr-2 outline-none cursor-default"
              aria-label="Share URL"
            />
            <button
              onClick={handleCopy}
              className="text-gray-600 hover:text-gray-800 focus:outline-none"
              aria-label="Copy URL"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>

          <div className="flex justify-between gap-x-2">
            <a
              href={`https://api.whatsapp.com/send?text=${encodeURIComponent(whatsappMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-2 py-1 text-sm rounded bg-green-500 text-white hover:bg-green-600"
            >
              <FaWhatsapp className="w-4 h-4" /> WhatsApp
            </a>
            <a
              href={`https://t.me/share/url?url=${encodeURIComponent(url)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-2 py-1 text-sm rounded bg-blue-500 text-white hover:bg-blue-600"
            >
              <FaTelegramPlane className="w-4 h-4" /> Telegram
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
